import type { CreateDeliveryBody } from '../api/types';

/**
 * Jalur TARIK — surat jalan yang dipilih manajer dari ERP. MURNI, tanpa React.
 *
 * Sisi web dari `ERP-SHIPMENT-PULL-CONTRACT.md`. Yang dijaga di sini bukan tampilan, melainkan
 * dua hal yang membuat jalur ini bertabrakan dengan benar terhadap jalur dorong.
 */

/** Sumber dan jenis entitas rujukan — kembar dengan konstanta di `erp-ingest.service.ts`. */
export const SUMBER_ERP = 'erp';
export const JENIS_SHIPMENT = 'shipment';

/**
 * Rujukan alat (installed base) untuk tiket servis — ADR-0054 Fase 2. Cocok dengan filter
 * riwayat servis di `field-task.repository.ts`, yang menyaring pada `externalRefEntityType='asset'`
 * + `externalRefEntityId`, MENGABAIKAN `sourceSystem`. `sourceSystem` tetap diisi agar asal tiket
 * terbaca manusia dan dapat dibedakan dari rujukan ERP.
 */
export const SUMBER_ALAT = 'workforce.asset';
export const JENIS_ALAT = 'asset';

/** Bentuk rujukan sistem luar — kembar dengan `externalRef` di `CreateTaskBody`/`ExternalRefDto`. */
export interface ExternalRef {
  readonly sourceSystem: string;
  readonly entityType: string;
  readonly entityId: string;
}

/**
 * Kunci idempotensi untuk tugas yang lahir dari DO tertentu.
 *
 * BENTUKNYA WAJIB IDENTIK dengan `kunciIdempotensi()` di `erp-ingest.service.ts`. Kalau keduanya
 * menyimpang satu karakter pun, dorongan ERP untuk DO yang sama tidak akan menabrak tugas ini —
 * dan yang lahir adalah tugas KEDUA untuk satu surat jalan, yakni kurir kedua yang berangkat
 * membawa barang yang sudah dikirim.
 *
 * Kontrak menyuruh memakai ulang fungsinya, bukan menulis ulang formatnya. Itu tidak mungkin di
 * sini: `workforce-web` dan `workforce-api` paket terpisah tanpa paket bersama, dan menambah
 * dependensi antar-paket demi satu template string adalah ongkos yang lebih besar daripada yang
 * dibelinya. Sebagai gantinya `tarik-do.spec.ts` MEMBACA fungsi aslinya dan menolak keduanya
 * berbeda — penjaga tekstual, bukan janji.
 */
export function kunciIdempotensiErp(shipmentId: string): string {
  return `${SUMBER_ERP}:${JENIS_SHIPMENT}:${shipmentId}`;
}

/**
 * Memilih externalRef untuk tugas baru — SATU tempat yang memutuskan ke apa tugas menunjuk.
 *
 * URUTAN WAJIB: surat jalan ERP MENANG atas alat. Tugas yang lahir dari surat jalan membawa
 * rujukan ERP + kunci idempotensi `erp:shipment:<id>` yang membuat dorongan ERP menabrak tugas
 * ini alih-alih melahirkan tugas kedua (lihat `kunciIdempotensiErp`). Karena itu bila ada surat
 * jalan terpilih, `assetId` TIDAK boleh menggesernya — walau keduanya kebetulan terisi. Tiket
 * servis (non-pengiriman) tidak punya surat jalan, jadi di praktiknya keduanya tak pernah
 * bersaing; urutan di sini yang menjamin itu tetap benar seandainya bersaing.
 *
 * `assetId` kosong atau hanya spasi diperlakukan sebagai TIDAK ADA — `?assetId=` tanpa nilai
 * tidak menautkan tugas ke alat mana pun.
 */
export function externalRefUntukTugas(args: {
  readonly suratTerpilih: SuratJalanErp | null;
  readonly assetId: string | null | undefined;
}): ExternalRef | null {
  if (args.suratTerpilih) {
    return {
      sourceSystem: SUMBER_ERP,
      entityType: JENIS_SHIPMENT,
      entityId: args.suratTerpilih.shipmentId,
    };
  }
  const alat = args.assetId?.trim();
  if (alat && alat.length > 0) {
    return { sourceSystem: SUMBER_ALAT, entityType: JENIS_ALAT, entityId: alat };
  }
  return null;
}

/** Bentuk satu surat jalan sebagaimana dikirim `GET /api/erp/shipments`. */
export interface SuratJalanErp {
  readonly shipmentId: string;
  readonly deliveryOrderNumber?: string | null;
  readonly customerName?: string | null;
  readonly contactPhone?: string | null;
  readonly destinationAddress?: string | null;
  readonly deliveryNotes?: string | null;
  readonly shipDate?: string | null;
  readonly items: readonly {
    readonly lineNo: number;
    readonly itemName: string;
    readonly sku?: string | null;
    readonly quantity: string;
    readonly unit?: string | null;
  }[];
  /** Dijawab SERVER dari data Workforce, bukan dari ERP — lihat kontrak §4.2. */
  readonly sudahAdaTugas: boolean;
  readonly taskNumber: string | null;
}

/** Label satu baris untuk pemilih DO. Menyebut nomor DAN pelanggan — nomor saja tidak dikenali. */
export function labelSuratJalan(s: SuratJalanErp): string {
  const nomor = s.deliveryOrderNumber?.trim() || `#${s.shipmentId}`;
  const pelanggan = s.customerName?.trim();
  const dasar = pelanggan ? `${nomor} — ${pelanggan}` : nomor;
  /*
   * DO yang sudah punya tugas TIDAK disembunyikan, melainkan DITANDAI. Menyembunyikannya membuat
   * manajer yang mencarinya menyimpulkan DO-nya hilang dari ERP, lalu mencarinya di tempat yang
   * salah. Yang ditandai dapat dijelaskan; yang hilang hanya dapat ditebak.
   */
  return s.sudahAdaTugas ? `${dasar}  ·  sudah ada tugas ${s.taskNumber ?? ''}`.trimEnd() : dasar;
}

/** Judul tugas dari DO — sama bentuknya dengan yang dipakai jalur dorong. */
export function judulDariSuratJalan(s: SuratJalanErp): string {
  const nomor = s.deliveryOrderNumber?.trim();
  return nomor ? `Pengiriman ${nomor}` : 'Pengiriman';
}

/**
 * Jumlah untuk DIBACA MANUSIA — "30.0000" menjadi "30".
 *
 * ERP mengirim empat desimal untuk setiap jumlah, termasuk yang bulat. Manajer yang membaca
 * "2.0000" harus berhenti sejenak menghitung nolnya untuk memastikan ia dua, bukan dua ribu; dan
 * kolomnya jadi penuh angka yang tidak membawa keterangan apa pun.
 *
 * HANYA UNTUK TAMPILAN. Yang dikirim ke server saat DO dipilih tetap string ASLI dari ERP —
 * badan permintaannya dibangun dari `blokPengirimanDari(suratTerpilih)`, bukan dari isian layar.
 * Jadi penyederhanaan ini tidak pernah menyentuh data yang tersimpan.
 *
 * JEBAKANNYA nol yang BUKAN di belakang koma. "100" tidak boleh menjadi "1", dan "1000.0000"
 * harus menjadi "1000", bukan "1". Karena itu pemangkasan hanya dilakukan bila ada titik desimal,
 * dan hanya pada bagian sesudahnya. Desimal sungguhan tetap utuh: "2.5000" menjadi "2.5",
 * "0.7500" menjadi "0.75" — membuangnya barulah kehilangan presisi.
 *
 * Bentuk yang tidak dikenali dikembalikan APA ADANYA. Fungsi ini merapikan, dan yang tidak dapat
 * dirapikan dengan yakin lebih baik ditampilkan asli daripada ditebak.
 */
export function jumlahRingkas(jumlah: string): string {
  const t = jumlah.trim();
  if (!/^\d+\.\d+$/.test(t)) return jumlah;
  const [bulat, pecahan] = t.split('.') as [string, string];
  const sisa = pecahan.replace(/0+$/, '');
  return sisa.length > 0 ? `${bulat}.${sisa}` : bulat;
}

/**
 * Mengubah surat jalan ERP menjadi blok pengiriman yang diterima `POST /api/field-tasks`.
 *
 * Nilai kosong dijadikan `null`, bukan string kosong: DTO server menolak sebagian field kosong,
 * dan `""` yang lolos tetap menghasilkan alamat kosong yang dibaca kurir sebagai alamat.
 *
 * `quantity` diteruskan APA ADANYA sebagai string. Mengubahnya ke number di sini akan memotong
 * presisi desimal yang sengaja dijaga sepanjang rantai ini — "24.0000" tidak boleh menjadi 24.
 */
export function blokPengirimanDari(s: SuratJalanErp): CreateDeliveryBody {
  const bersih = (v: string | null | undefined): string | null => {
    const t = v?.trim();
    return t && t.length > 0 ? t : null;
  };
  /*
   * TANPA `as`. Versi pertama fungsi ini mengembalikan blok `dropoff` dan meng-cast hasilnya ke
   * `CreateDeliveryBody` — cast itu MENYEMBUNYIKAN bahwa `DeliveryDto` di server tidak punya
   * `dropoff` sama sekali, hanya `pickup`. `forbidNonWhitelisted` akan menolak SELURUH permintaan
   * dengan 400, dan tidak ada satu pun typecheck yang berkeberatan sampai orang menekan Simpan.
   *
   * Itu kelas cacat yang sama dengan `externalRef` di kontrak ingest kemarin: satu field asing
   * membatalkan seluruh badan. Tipe yang dikembalikan apa adanya membuat TypeScript yang
   * menangkapnya, bukan pemakainya.
   *
   * Alamat tujuan TIDAK masuk blok ini — ia hidup di `destination` tingkat atas pada badan
   * pembuatan tugas, dan layar sudah mengisinya lewat `destinationName`/`destinationAddress`.
   */
  return {
    deliveryOrderNumber: bersih(s.deliveryOrderNumber),
    customerName: bersih(s.customerName),
    contactPhone: bersih(s.contactPhone),
    deliveryNotes: bersih(s.deliveryNotes),
    items: s.items.map((i) => ({
      lineNo: i.lineNo,
      itemName: i.itemName,
      quantity: i.quantity,
      sku: bersih(i.sku),
      unit: bersih(i.unit),
    })),
  };
}
